import AppLayout from "jadro-pico-app/Components/UI/Layouts/AppLayout/AppLayout";
import AppBody from "jadro-pico-app/Components/UI/Layouts/AppLayout/Sections/AppBody/AppBody";
import AppContent from "jadro-pico-app/Components/UI/Layouts/AppLayout/Sections/AppContent/AppContent";
import AppHeader from "jadro-pico-app/Components/UI/Layouts/AppLayout/Sections/AppHeader/AppHeader";
import { DarkTheme } from "jadro-pico-app/Components/UI/Themes/DarkTheme";
import { GlobalAppTheme } from "jadro-pico-app/Components/UI/Themes/GlobalAppTheme";
import { LightTheme } from "jadro-pico-app/Components/UI/Themes/LightTheme";
import { ThemeProvider } from "jadro-pico-app/Components/UI/Themes/ThemeProvider";
import { FabProvider } from "jadro-pico-app/Context/FabContext";
import PicoLogo from "jadro-pico-app/Components/UI/PicoLogo/PicoLogo";
import AppsLauncher from "jadro-pico-app/Components/UI/AppsLauncher/AppsLauncher";
import ProfileMenu from "jadro-pico-app/Components/UI/ProfileMenu/ProfileMenu";
import RequestQueueHub from "jadro-pico-app/Components/UI/RequestQueue/RequestQueueHub";
import { PicoAppEnum } from "jadro-pico/apps/PicoAppEnum";
import { useBootstrapAuth } from "jadro-pico-app/Auth/useBootstrapAuth";
import { useFavicon } from "jadro-pico-app/Hooks/useFavicon";
import { ApiPaths } from "jadro-pico/api-endpoints/ApiPaths";
import picoLogoUrl from "jadro-pico-app/Components/UI/PicoLogo/PicoLogo.svg";
import { useApiQuery } from "jadro-pico-app/Api/useApiQuery";
import { validateReturnTo } from "jadro-pico/apps/validateReturnTo";
import { PicoAppDto } from "jadro-pico/apps/PicoAppDto";
import { useSelector } from "react-redux";
import {
  Navigate,
  Route,
  Routes as ReactRouterRoutes,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router";
import { renderRequestQueueRoutes } from "jadro-pico-app/Routes/renderRequestQueueRoutes";
import { renderRouteAliases } from "jadro-pico-app/Routes/renderRouteAliases";
import { useRoutePaths } from "jadro-pico-app/Routes/useRoutePaths";
import { hasSomewhereToGoBack } from "jadro-pico-app/Routes/hasSomewhereToGoBack";
import { PicoComRoutes } from "jadro-pico-app/Routes/PicoComRoutes";
import { AppAuthStatusEnum } from "jadro-pico-app/Redux/AppAuthReducer";
import { RootState } from "./Redux/AppStore";
import Login from "./Components/Login/Login";
import Dashboard from "./Components/Dashboard/Dashboard";
import Profile from "./Components/Profile/Profile";
import { QueryKeys } from "./queryKeys";

function App() {
  // Not `user`: it is null while the bootstrap request is still in flight, and redirecting on that
  // would throw a signed-in visitor out to the login page on every load.
  const authStatus = useSelector((state: RootState) => state.auth.status);
  const signedOut = authStatus === AppAuthStatusEnum.UNAUTHENTICATED;
  const theme = useSelector((state: RootState) =>
    state.core.colorThemeName == LightTheme.name ? LightTheme : DarkTheme,
  );
  const paths = useRoutePaths(PicoComRoutes);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isDashboard = location.pathname === paths.DASHBOARD;
  useFavicon(picoLogoUrl);
  useBootstrapAuth(
    `${import.meta.env.VITE_PICO_API_URL}${ApiPaths.auth.prefix}${ApiPaths.auth.user}`,
  );

  const { data: apps } = useApiQuery<PicoAppDto[]>({
    url: `${import.meta.env.VITE_PICO_API_URL}/apps`,
    queryKey: QueryKeys.appsCatalog,
    staleTime: Infinity,
  });

  const goBack = () => {
    if (hasSomewhereToGoBack()) {
      navigate(-1);
      return;
    }
    const returnTo = searchParams.get("returnTo");
    const allowedOrigins = apps?.map((app) => new URL(app.feUrl).origin) ?? [];
    const target = returnTo && validateReturnTo(returnTo, allowedOrigins);
    if (target) window.location.href = target;
    else navigate(paths.DASHBOARD);
  };

  return (
    <ThemeProvider theme={theme}>
      <FabProvider>
        <GlobalAppTheme />
        <AppLayout>
          <AppHeader isLeftNavButtonVisible={false} appName="" logo={<PicoLogo size={28} />}>
            <AppsLauncher />
            <ProfileMenu />
          </AppHeader>
          <AppBody $isFullscreen={!isDashboard}>
            <AppContent isFullscreen={!isDashboard} symmetricMargin={isDashboard}>
              <ReactRouterRoutes>
                <Route path={paths.DASHBOARD} element={<Dashboard />} />
                <Route path={paths.LOGIN} element={<Login />} />
                <Route
                  path={paths.PROFILE}
                  element={
                    signedOut ? <Navigate to={paths.LOGIN} replace /> : <Profile onClose={goBack} />
                  }
                />
                {renderRequestQueueRoutes({
                  app: PicoAppEnum.PICO_COM,
                  apiUrl: import.meta.env.VITE_PICO_API_URL,
                  paths,
                  signedOut,
                  signedOutFallback: <Navigate to={paths.LOGIN} replace />,
                  hub: (
                    <RequestQueueHub
                      apiUrl={import.meta.env.VITE_PICO_API_URL}
                      app={PicoAppEnum.PICO_COM}
                      ownQueuePath={paths.REQUEST_QUEUE_MINE}
                    />
                  ),
                })}
                {renderRouteAliases(PicoComRoutes, paths)}
              </ReactRouterRoutes>
            </AppContent>
          </AppBody>
        </AppLayout>
      </FabProvider>
    </ThemeProvider>
  );
}

export default App;
